<template>
  <div class="view view-activate">
    <div class="view-auth__form">
      <logo class="view-auth__logo" />
      <template v-if="data.userDataError">
        <p class="ta-center">{{ data.userDataError }}</p>
        <div class="ta-center">
          <router-link :to="{ name: 'reset-password' }">{{
            $t("actions.resetPassword")
          }}</router-link>
        </div>
      </template>
      <template v-else-if="data.userData">
        <form
          ref="form"
          class="view-activate__form"
          @submit.prevent="changePassword"
        >
          <h1 class="title">{{ $t("headings.setNewPassword") }}</h1>
          <p class="mt-0">
            {{ $t("texts.setNewPassword") }}
          </p>
          <input-password
            v-model:value="data.password1"
            :label="$t('labels.password')"
            autocomplete="new-password"
            :required="true"
            :error="data.errors.password1"
            @update:value="updateFieldErrors('password1')"
          />
          <input-password
            v-model:value="data.password2"
            :label="$t('labels.passwordRepeat')"
            autocomplete="new-password"
            :required="true"
            :error="data.errors.password2"
            @update:value="updateFieldErrors('password2')"
          />

          <button class="button mt-4" type="submit">
            {{ $t("actions.setPassword") }}
          </button>

          <div v-if="data.errors.form" class="message-error mt-4">
            {{ data.errors.form }}
          </div>
        </form>
      </template>
      <template v-else>
        <p class="ta-center">
          {{ $t("messages.loadingData") }}
        </p>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { reactive, computed } from "vue";
import { get, post, saveToken } from "@/api/API";

import Logo from "@core/ui/SimcisionLogo.vue";
import InputPassword from "@core/input/InputPassword.vue";
import { useUser } from "@/api/user.store";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";

const props = defineProps<{
  uid: string;
  token: string;
}>();

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { loadUser } = useUser();

const data = reactive({
  password1: "",
  password2: "",
  userData: "",
  userDataError: null as string | null,
  errors: {
    password1: "",
    password2: "",
    form: ""
  }
});

getUserData();

const redirectPath = computed(() => {
  if (route.redirectedFrom) {
    return {
      path: route.redirectedFrom.path,
      params: route.redirectedFrom.params
    };
  }
  return {
    name: "models-explorer"
  };
});

async function getUserData() {
  try {
    data.userData = await get(
      `/reset_password/${props.uid}/${props.token}/`,
      { sendAuthToken: false }
    );
  } catch (e) {
    const error = e as Error & { status: number };
    console.log(error);
    if (error.status === 403) {
      data.userDataError = t(
        "errors.apiExceptionDetails.link_expired"
      );
    } else {
      data.userDataError = error.message;
    }
  }
}

async function changePassword() {
  try {
    data.errors.form = "";
    data.errors.password1 = "";
    data.errors.password2 = "";
    const tokenData = await post(`/reset_password/${props.uid}/${props.token}/`, {
      data: {
        password1: data.password1,
        password2: data.password2
      },
      sendAuthToken: false,
      toSnakeCase: false
    });
    saveToken(tokenData);

    await loadUser();
    router.push(redirectPath.value);
  } catch (e) {
    const error = e as Error & { fields: Record<string, string> };

    if (error.fields.password1) {
      data.errors.password1 = error.fields.password1;
    } else if (error.fields.password2) {
      data.errors.password2 = error.fields.password2;
    } else if (error.message) {
      data.errors.form = error.message;
    } else {
      throw error;
    }
  }
}

function updateConfirmError() {
  if (!data.errors.password2) return;
  if (data.password1 !== data.password2) {
    data.errors.password2 = t("errors.passwordsDoNotMatch");
  } else {
    data.errors.password2 = "";
  }
}

function updateFieldErrors(field: string) {
  if (field === "password1") data.errors.password1 = "";
  updateConfirmError();
}
</script>

<style lang="scss">
.view-activate {
  padding: 1rem;
  flex-direction: column;

  &__logo {
    margin: 0 auto;
  }

  &__form {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
}
</style>
